import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, Lock, RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { createCheckout, photoUrl, retryConcept, uploadPhoto } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function PhotoPicker({ photos, picked, setPicked, max }) {
  const toggle = (id) => {
    if (picked.includes(id)) return setPicked(picked.filter((p) => p !== id));
    if (picked.length >= max) return toast.error(`You can choose up to ${max} photo(s).`);
    setPicked([...picked, id]);
  };
  return (
    <div className="grid grid-cols-4 gap-2">
      {photos.map((p) => (
        <button key={p.id} type="button" data-testid={`pick-photo-${p.id}`} onClick={() => toggle(p.id)}
          className={`relative rounded-lg overflow-hidden border-2 ${picked.includes(p.id) ? "border-emerald-600" : "border-transparent"}`}>
          <img src={photoUrl(p.id)} alt="Your property" className="aspect-square w-full object-cover" />
          {picked.includes(p.id) && <CheckCircle2 size={18} className="absolute top-1 right-1 text-white drop-shadow" />}
        </button>
      ))}
    </div>
  );
}

function Option({ testId, title, price, desc, active, onClick }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      className={`text-left rounded-xl border p-3 transition-colors ${active ? "border-emerald-600 bg-emerald-50" : "border-slate-200 bg-white hover:border-emerald-300"}`}>
      <div className="flex justify-between gap-2 font-semibold text-[#092C15]"><span>{title}</span><span>{money(price)}</span></div>
      <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
    </button>
  );
}

export function FailedConceptNotice({ concept, onRetried }) {
  const [busy, setBusy] = useState(false);
  const canRetry = concept.kind !== "free" && concept.customer_retries < 1;
  const retry = async () => {
    setBusy(true);
    try {
      await retryConcept(concept.id);
      onRetried();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Retry isn't available.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={`concept-failed-${concept.id}`} className="rounded-xl border border-rose-200 bg-white p-3 text-sm">
      <div className="flex gap-2 font-semibold text-rose-700"><AlertCircle size={16} className="mt-0.5" /> Version {concept.version} couldn't be completed</div>
      <p className="text-slate-600 mt-1">Your purchase is saved and you won't be charged again. GreenFlow has been notified.</p>
      {canRetry && (
        <Button data-testid={`concept-paid-retry-${concept.id}`} size="sm" variant="outline" disabled={busy} onClick={retry} className="mt-2 rounded-full">
          {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RefreshCw size={14} className="mr-1.5" />} Try again at no charge
        </Button>
      )}
    </div>
  );
}

export default function MoreViews({ session, setSession, data, refresh }) {
  const { user } = useAuth();
  const [item, setItem] = useState("");
  const [picked, setPicked] = useState([]);
  const [target, setTarget] = useState("");
  const [adjustment, setAdjustment] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const concepts = data.concepts || [];
  const visualized = new Set(concepts.filter((c) => !c.parent_concept_id).map((c) => c.source_photo_id));
  const available = (session.photos || []).filter((p) => !visualized.has(p.id));
  const remaining = Math.max(data.max_views - data.views_used, 0);
  const revisedIds = new Set(concepts.filter((c) => c.parent_concept_id).map((c) => c.parent_concept_id));
  const revisable = concepts.filter((c) => c.status === "complete" && !c.parent_concept_id && !revisedIds.has(c.id));
  const max = item === "extra_view" ? 1 : remaining;

  const addPhotos = async (files) => {
    let s = session;
    for (const f of Array.from(files || [])) {
      try {
        const meta = await uploadPhoto(session.id, f, false, "Additional view");
        s = { ...s, photos: [...(s.photos || []), meta] };
      } catch (ex) {
        toast.error(ex?.response?.data?.detail || "Upload failed");
      }
    }
    setSession(s);
    if (fileRef.current) fileRef.current.value = "";
  };

  const pay = async () => {
    setBusy(true);
    try {
      const body = item === "revision"
        ? { session_id: session.id, item, target_concept_id: target, adjustment, comment }
        : { session_id: session.id, item, photo_ids: picked };
      const { checkout_url } = await createCheckout(body);
      window.location.href = checkout_url;
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };
  const ready = item === "revision" ? revisable.some((c) => c.id === target) && adjustment && (adjustment !== "Other" || comment.trim()) : picked.length > 0;
  const missing = !item || ready ? "" : item !== "revision" ? "Choose at least one photo above to continue."
    : !revisable.length ? "" : !revisable.some((c) => c.id === target) ? "Tap the concept version you want revised." : !adjustment ? "Tap one main adjustment above (for example Reduce the scope or Other)."
    : "Add a short comment for Other.";

  return (
    <section data-testid="more-views-panel" className="rounded-2xl border border-emerald-200 bg-white p-5">
      <h3 className="font-display text-lg font-bold text-[#092C15]">Explore More of Your Property</h3>
      <p className="text-sm text-slate-600 mt-1">Your first concept is free. Add more views or one revision — pay only for what you choose. No subscription.</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        <Option testId="option-extra-view" title="Additional View" price={data.prices.extra_view} desc="One more photo of your property" active={item === "extra_view"} onClick={() => { setItem("extra_view"); setPicked([]); }} />
        <Option testId="option-whole-property" title="Whole Property" price={data.prices.whole_property} desc={`Up to ${data.max_views} total views`} active={item === "whole_property"} onClick={() => { setItem("whole_property"); setPicked([]); }} />
        <Option testId="option-revision" title="Revised Concept" price={data.prices.revision} desc="One adjustment to a concept" active={item === "revision"} onClick={() => { setItem("revision"); const pick = revisable[revisable.length - 1]; if (!revisable.some((c) => c.id === target)) setTarget(pick ? pick.id : ""); }} />
      </div>

      {(item === "extra_view" || item === "whole_property") && (
        <div className="mt-4 space-y-3" data-testid="view-picker">
          {remaining === 0 ? (
            <p className="text-sm text-slate-600">You've reached the maximum of {data.max_views} views for this submission.</p>
          ) : (
            <>
              <div className="text-sm font-semibold text-[#092C15]">Choose {item === "extra_view" ? "1 photo" : `up to ${max} photos`} to visualize</div>
              {available.length > 0 ? <PhotoPicker photos={available} picked={picked} setPicked={setPicked} max={max} /> : <p className="text-sm text-slate-500">Add another photo of your property to continue.</p>}
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="more-views-photo-input" onChange={(e) => addPhotos(e.target.files)} />
              <Button data-testid="more-views-add-photo" variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="rounded-full"><ImagePlus size={14} className="mr-1.5" /> Add a photo</Button>
            </>
          )}
        </div>
      )}

      {item === "revision" && (
        <div className="mt-4 space-y-3" data-testid="revision-picker">
          <div className="text-sm font-semibold text-[#092C15]">Which concept should we revise?</div>
          {revisable.length === 0 && <p className="text-sm text-slate-500">Each concept can be revised once. All of your concepts have already been revised.</p>}
          <div className="flex flex-wrap gap-2">
            {revisable.map((c) => (
              <button key={c.id} type="button" data-testid={`revise-target-${c.version}`} onClick={() => setTarget(c.id)}
                className={`rounded-full border px-3 py-1.5 text-sm ${target === c.id ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200"}`}>Version {c.version}</button>
            ))}
          </div>
          <div className="text-sm font-semibold text-[#092C15]">Choose one main adjustment</div>
          <div className="flex flex-wrap gap-2">
            {(data.revision_options || []).map((o) => (
              <button key={o} type="button" data-testid={`paid-revision-option-${slug(o)}`} onClick={() => setAdjustment(o)}
                className={`rounded-full border px-3 py-1.5 text-sm ${adjustment === o ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200"}`}>{o === "Other" ? "Other (add a comment)" : o}</button>
            ))}
          </div>
          <Textarea data-testid="paid-revision-comment" rows={2} maxLength={1000} placeholder="Short comment (required for Other)" value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>
      )}

      {missing && <p data-testid="checkout-missing-hint" className="mt-3 text-sm font-medium text-amber-700">{missing}</p>}
      {item && (
        <Button data-testid="checkout-button" disabled={!ready || busy} onClick={pay} className="mt-4 w-full h-12 rounded-xl bg-[#092C15] hover:bg-emerald-900 font-semibold">
          {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Lock size={16} className="mr-2" />} {user?.is_admin ? "Create Free (Admin — no card)" : `Pay ${money(data.prices[item])} Securely with Stripe`}
        </Button>
      )}
      <p className="mt-2 text-[11px] text-slate-500 text-center">{user?.is_admin ? "Admin — created free, no payment taken." : "One-time payment. Your image is created only after payment is verified."}</p>
    </section>
  );
}
